import type { User, OrgRole, UserWorkspace, UserStatus } from "@/types/user";

import { faker } from "@faker-js/faker";

import {
  getMockMarket,
  getMockMarketProfile,
  type MockMarket,
} from "../market";

import {
  createId,
  createPastDate,
  createAvatarUrl,
  createEmail,
  createPhoneNumber,
  generateLocalName,
} from "./base";

import { ORG_ROLE, USER_STATUS, WORKSPACE_ROLE } from "@/types/user";

// Mock workspaces for user assignments
const MOCK_WORKSPACES = [
  { id: "ws-1", name: "Marketing" },
  { id: "ws-2", name: "Sales" },
  { id: "ws-3", name: "Support" },
];

// Generate random workspace assignments for a user
function generateWorkspaceAssignments(isAdmin: boolean): UserWorkspace[] {
  if (isAdmin) {
    // Admins don't need workspace assignments
    return [];
  }

  // Randomly assign 1-3 workspaces (seeded faker so data is deterministic)
  const numWorkspaces = faker.number.int({ min: 1, max: 3 });
  const shuffled = faker.helpers.shuffle([...MOCK_WORKSPACES]);
  const selectedWorkspaces = shuffled.slice(0, numWorkspaces);

  const roles = [
    WORKSPACE_ROLE.ADMIN,
    WORKSPACE_ROLE.MEMBER,
    WORKSPACE_ROLE.AGENT,
  ];

  return selectedWorkspaces.map((ws) => ({
    id: ws.id,
    name: ws.name,
    role: faker.helpers.arrayElement(roles),
  }));
}

interface UserConfig {
  firstName: string;
  lastName: string;
  role: OrgRole;
  title?: string;
  status?: UserStatus;
}

const AE_USERS: UserConfig[] = [
  {
    firstName: "Fatima",
    lastName: "Al-Mansoori",
    role: ORG_ROLE.ADMIN,
    title: "Clinic Manager",
  },
  {
    firstName: "Omar",
    lastName: "Al-Nuaimi",
    role: ORG_ROLE.MEMBER,
    title: "Receptionist",
  },
  {
    firstName: "Mariam",
    lastName: "Al-Falasi",
    role: ORG_ROLE.MEMBER,
    title: "Treatment Coordinator",
  },
  {
    firstName: "Khalid",
    lastName: "Al-Suwaidi",
    role: ORG_ROLE.MEMBER,
    title: "Patient Coordinator",
  },
  {
    firstName: "Aisha",
    lastName: "Al-Hammadi",
    role: ORG_ROLE.MEMBER,
    title: "Appointment Specialist",
  },
  {
    firstName: "Yousef",
    lastName: "Al-Mazrouei",
    role: ORG_ROLE.MEMBER,
    title: "Office Administrator",
  },
  {
    firstName: "Priya",
    lastName: "Nair",
    role: ORG_ROLE.MEMBER,
    title: "Patient Care Rep",
    status: USER_STATUS.PENDING,
  },
  {
    firstName: "Daniel",
    lastName: "Fernandes",
    role: ORG_ROLE.MEMBER,
    title: "Front Desk Staff",
    status: USER_STATUS.EXPIRED,
  },
];

const NEPALI_USERS: UserConfig[] = [
  {
    firstName: "Anjali",
    lastName: "Shrestha",
    role: ORG_ROLE.ADMIN,
    title: "Clinic Manager",
  },
  {
    firstName: "Nabin",
    lastName: "Gurung",
    role: ORG_ROLE.MEMBER,
    title: "Receptionist",
  },
  {
    firstName: "Sunita",
    lastName: "Thapa",
    role: ORG_ROLE.MEMBER,
    title: "Treatment Coordinator",
  },
  {
    firstName: "Bishal",
    lastName: "Adhikari",
    role: ORG_ROLE.MEMBER,
    title: "Patient Coordinator",
  },
  {
    firstName: "Puja",
    lastName: "Rai",
    role: ORG_ROLE.MEMBER,
    title: "Appointment Specialist",
  },
  {
    firstName: "Prakash",
    lastName: "Karki",
    role: ORG_ROLE.MEMBER,
    title: "Office Administrator",
  },
  {
    firstName: "Sita",
    lastName: "Tamang",
    role: ORG_ROLE.MEMBER,
    title: "Patient Care Rep",
    status: USER_STATUS.PENDING,
  },
  {
    firstName: "Sujan",
    lastName: "Poudel",
    role: ORG_ROLE.MEMBER,
    title: "Front Desk Staff",
    status: USER_STATUS.EXPIRED,
  },
];

const USA_USERS: UserConfig[] = [
  {
    firstName: "Olivia",
    lastName: "Carter",
    role: ORG_ROLE.ADMIN,
    title: "Clinic Manager",
  },
  {
    firstName: "Ethan",
    lastName: "Brooks",
    role: ORG_ROLE.MEMBER,
    title: "Receptionist",
  },
  {
    firstName: "Maya",
    lastName: "Patel",
    role: ORG_ROLE.MEMBER,
    title: "Treatment Coordinator",
  },
  {
    firstName: "Noah",
    lastName: "Williams",
    role: ORG_ROLE.MEMBER,
    title: "Patient Coordinator",
  },
  {
    firstName: "Sofia",
    lastName: "Ramirez",
    role: ORG_ROLE.MEMBER,
    title: "Appointment Specialist",
  },
  {
    firstName: "Marcus",
    lastName: "Johnson",
    role: ORG_ROLE.MEMBER,
    title: "Office Administrator",
  },
  {
    firstName: "Chloe",
    lastName: "Bennett",
    role: ORG_ROLE.MEMBER,
    title: "Patient Care Rep",
    status: USER_STATUS.PENDING,
  },
  {
    firstName: "Daniel",
    lastName: "Kim",
    role: ORG_ROLE.MEMBER,
    title: "Front Desk Staff",
    status: USER_STATUS.EXPIRED,
  },
];

const MARKET_USERS: Record<MockMarket, UserConfig[]> = {
  nepal: NEPALI_USERS,
  uae: AE_USERS,
  usa: USA_USERS,
};

/**
 * Generate mock users for an organization
 */
export function generateUsers(
  organizationId: string,
  count: number = 8,
): User[] {
  const users: User[] = [];

  const market = getMockMarket();
  const profile = getMockMarketProfile(market);
  const seedUsers = MARKET_USERS[market];

  for (let i = 0; i < count; i++) {
    let config: UserConfig;

    if (i < seedUsers.length) {
      config = seedUsers[i];
    } else {
      const { firstName, lastName } = generateLocalName();

      config = {
        firstName,
        lastName,
        role: ORG_ROLE.MEMBER,
      };
    }

    const createdAt = createPastDate(60 - i * 5);

    const isAdmin = config.role === ORG_ROLE.ADMIN;
    const status = config.status ?? USER_STATUS.ACTIVE;
    const isPendingOrExpired =
      status === USER_STATUS.PENDING || status === USER_STATUS.EXPIRED;
    const invitationId = isPendingOrExpired ? createId() : undefined;

    users.push({
      id: createId(),
      firstName: config.firstName,
      lastName: config.lastName,
      email: createEmail(config.firstName, config.lastName),
      phone: createPhoneNumber(),
      imageUrl: createAvatarUrl(),
      externalId: createId(),
      organizationId,
      role: config.role,
      status,
      invitationId,
      title: config.title,
      timezone: profile.timezone,
      language: profile.locale,
      location: profile.location,
      biography: faker.lorem.sentence(),
      createdAt,
      updatedAt: createdAt,
      deletedAt: null,
      presence: {
        status: i < 4 ? "online" : "offline",
        lastSeenAt: new Date().toISOString(),
      },
      workspaces: generateWorkspaceAssignments(isAdmin),
    });
  }

  return users;
}
